import React, { useEffect, useMemo, useState, useTransition } from "react";
import * as XLSX from "xlsx";
import { FaChartLine, FaNetworkWired, FaUsers, FaExclamationTriangle } from "react-icons/fa";
import PageHeader from "../components/common/PageHeader";
import AuthService from "../services/AuthService";
import { getAuditLogModuleLabel } from "../Utils/auditLogModuleLabels";

const ROWS_PER_PAGE = 10;
const PAGES_TO_SHOW = 5;

const styles = {
  wrapper: {
    width: "100%",
    margin: 0,
    padding: "0 0 16px",
    boxSizing: "border-box",
    background: "transparent",
    fontFamily: "Poppins, sans-serif",
  },
  card: {
    width: "100%",
    background: "transparent",
    padding: "6px 2px 14px",
    borderRadius: 0,
    boxShadow: "none",
    border: "none",
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
    width: "100%",
    marginBottom: 18,
  },
  kpiItem: {
    background: "linear-gradient(135deg, #021a30 0%, #032B4E 55%, #032B4E 100%)",
    borderRadius: 11,
    padding: "12px 16px",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    color: "#ffffff",
    boxShadow: "0 3px 12px rgba(3, 43, 78, 0.13)",
    display: "flex",
    alignItems: "center",
    gap: 12,
    minHeight: 78,
    boxSizing: "border-box",
  },
  kpiIconWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "rgba(224, 102, 57, 0.18)",
  },
  kpiIcon: { fontSize: 18, color: "#E06639" },
  kpiContent: { minWidth: 0, flex: 1 },
  kpiItemH4: {
    fontSize: 12,
    fontWeight: 600,
    color: "rgba(255,255,255,0.82)",
    margin: "0 0 3px",
    lineHeight: 1.25,
    fontFamily: "Poppins, sans-serif",
  },
  kpiValue: {
    fontSize: 22,
    fontWeight: 700,
    color: "#ffffff",
    margin: 0,
    lineHeight: 1.1,
    fontFamily: "Poppins, sans-serif",
  },
  filterBar: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 14,
  },
  searchGroup: {
    flex: "1 1 260px",
    maxWidth: 380,
  },
  searchInput: {
    width: "100%",
    padding: "8px 12px",
    fontSize: 13,
    border: "1px solid #e6eef4",
    borderRadius: 8,
    background: "#fff",
    color: "#0c3c54",
    boxSizing: "border-box",
    fontFamily: "Poppins, sans-serif",
    outline: "none",
    fontWeight: 500,
  },
  filterActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
  },
  select: {
    padding: "8px 34px 8px 12px",
    borderRadius: 8,
    border: "none",
    backgroundColor: "#6b7280",
    backgroundImage:
      'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'8\' viewBox=\'0 0 12 8\' fill=\'none\'%3E%3Cpath d=\'M1 1.5L6 6.5L11 1.5\' stroke=\'white\' stroke-width=\'1.8\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")',
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 11px center",
    backgroundSize: "11px 7px",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "Poppins, sans-serif",
    cursor: "pointer",
    outline: "none",
  },
  resetBtn: {
    padding: "8px 14px",
    borderRadius: 8,
    border: "none",
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "Poppins, sans-serif",
    cursor: "pointer",
    background: "#E06639",
    boxShadow: "0 2px 8px rgba(224, 102, 57, 0.22)",
  },
  downloadBtn: {
    padding: "8px 14px",
    borderRadius: 8,
    border: "none",
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "Poppins, sans-serif",
    cursor: "pointer",
    background: "#032B4E",
  },
  btnDisabled: {
    background: "#9ca3af",
    cursor: "not-allowed",
    boxShadow: "none",
  },
  tableWrap: {
    width: "100%",
    borderRadius: 10,
    border: "2px solid #032B4E",
    boxShadow: "0 2px 8px rgba(12, 60, 84, 0.06)",
    background: "#032B4E",
    overflow: "hidden",
  },
  tableScroll: {
    width: "100%",
    overflowX: "auto",
    background: "#fff",
  },
  table: {
    width: "100%",
    minWidth: 920,
    borderCollapse: "collapse",
    fontFamily: "Poppins, sans-serif",
  },
  th: {
    padding: "9px 12px",
    textAlign: "center",
    fontSize: 12,
    fontWeight: 700,
    color: "#ffffff",
    background: "#032B4E",
    border: "1px solid #032B4E",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "8px 12px",
    textAlign: "center",
    fontSize: 12,
    fontWeight: 500,
    color: "#6a7d8c",
    border: "1px solid #e2eaf0",
    verticalAlign: "middle",
  },
  tdBold: {
    padding: "8px 12px",
    textAlign: "center",
    fontSize: 12,
    fontWeight: 600,
    color: "#0c3c54",
    border: "1px solid #e2eaf0",
    verticalAlign: "middle",
  },
  tdLeft: {
    padding: "8px 12px",
    textAlign: "left",
    fontSize: 12,
    fontWeight: 500,
    color: "#6a7d8c",
    border: "1px solid #e2eaf0",
    verticalAlign: "middle",
    minWidth: 300,
    maxWidth: 440,
  },
  detailChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 5,
  },
  detailChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    maxWidth: "100%",
    padding: "3px 8px",
    borderRadius: 6,
    background: "#f6fafd",
    border: "1px solid #e6eef4",
    fontSize: 10,
    lineHeight: 1.3,
    fontFamily: "Poppins, sans-serif",
  },
  detailKey: {
    color: "#032B4E",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  detailValue: {
    color: "#6a7d8c",
    fontWeight: 500,
    wordBreak: "break-word",
  },
  detailEmpty: {
    color: "#9ca3af",
    fontSize: 12,
    fontFamily: "Poppins, sans-serif",
  },
  metaRow: {
    marginTop: 10,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    fontFamily: "Poppins, sans-serif",
    fontSize: 12,
    color: "#6a7d8c",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 14,
  },
  pageBtn: {
    padding: "7px 12px",
    borderRadius: 8,
    border: "1px solid #e6eef4",
    background: "#fff",
    color: "#0c3c54",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "Poppins, sans-serif",
    cursor: "pointer",
  },
  pageBtnActive: {
    background: "#E06639",
    color: "#fff",
    borderColor: "#E06639",
    boxShadow: "0 2px 8px rgba(224, 102, 57, 0.22)",
  },
};

const getStatusMeta = (status, label) => {
  if (status >= 200 && status < 300) {
    return { bg: "#059669", color: "#fff", text: label || "SUCCESS" };
  }
  if (status >= 400) {
    return { bg: "#6b7280", color: "#fff", text: label || "FAILED" };
  }
  return { bg: "#E06639", color: "#fff", text: label || "INFO" };
};

const trimQuotes = (value) =>
  typeof value === "string" ? value.trim().replace(/^['"]|['"]$/g, "") : value;

const formatDateLocal = (value) => {
  if (!value) return "Unknown Date";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "Unknown Date";
  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatTimeLocal = (value) => {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleTimeString("en-IN", { hour12: false });
};

const safeJsonParse = async (response) => {
  try {
    return await response.json();
  } catch (e) {
    return null;
  }
};

const getReadableAction = (eventType) => {
  const actionMap = {
    LOGIN: "User signed in",
    LOGIN_ERROR: "Login failed",
    LOGOUT: "User signed out",
    LOGOUT_ERROR: "Logout failed",
    REGISTER: "User registered",
    REGISTER_ERROR: "Registration failed",
    UPDATE_PASSWORD: "Password updated",
    UPDATE_PASSWORD_ERROR: "Password update failed",
    RESET_PASSWORD: "Password reset",
    RESET_PASSWORD_ERROR: "Password reset failed",
    UPDATE_PROFILE: "Profile updated",
    UPDATE_PROFILE_ERROR: "Profile update failed",
    VERIFY_EMAIL: "Email verified",
    VERIFY_EMAIL_ERROR: "Email verification failed",
    CODE_TO_TOKEN: "Token issued",
    REFRESH_TOKEN: "Session refreshed",
    REFRESH_TOKEN_ERROR: "Session refresh failed",
    CLIENT_LOGIN: "Client login",
    CLIENT_LOGIN_ERROR: "Client login failed",
    REVOKE_GRANT: "Access revoked",
    REVOKE_GRANT_ERROR: "Access revoke failed",
  };

  return actionMap[eventType] || eventType?.replace(/_/g, " ") || "System activity";
};

const AUTH_SIGN_ACTIONS = new Set([
  "User signed in",
  "User signed out",
  "Login failed",
  "Logout failed",
]);

const isAuthSignAction = (action) => AUTH_SIGN_ACTIONS.has(action);

const getVisiblePages = (currentPage, totalPages) => {
  let startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(totalPages, startPage + PAGES_TO_SHOW - 1);

  if (endPage - startPage + 1 < PAGES_TO_SHOW) {
    startPage = Math.max(1, endPage - PAGES_TO_SHOW + 1);
  }

  return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
};

const KpiCard = ({ Icon, label, value }) => (
  <div style={styles.kpiItem}>
    <div style={styles.kpiIconWrap}>
      <Icon style={styles.kpiIcon} />
    </div>
    <div style={styles.kpiContent}>
      <h4 style={styles.kpiItemH4}>{label}</h4>
      <p style={styles.kpiValue}>{value}</p>
    </div>
  </div>
);

const StatusTag = ({ statusCode, label }) => {
  const meta = getStatusMeta(statusCode, label);
  return (
    <span
      style={{
        display: "inline-block",
        minWidth: 100,
        backgroundColor: meta.bg,
        color: meta.color,
        fontWeight: 700,
        padding: "5px 10px",
        borderRadius: 999,
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: "0.4px",
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {(meta.text || "-").toUpperCase()}
    </span>
  );
};

const parseDetailParts = (details) => {
  if (!details || details === "-") return [];
  return String(details)
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const colonIdx = part.indexOf(":");
      if (colonIdx === -1) return { key: null, value: part };
      return {
        key: part.slice(0, colonIdx).trim(),
        value: part.slice(colonIdx + 1).trim(),
      };
    });
};

const DetailsCell = ({ details }) => {
  const parts = parseDetailParts(details);
  if (!parts.length) {
    return <span style={styles.detailEmpty}>-</span>;
  }

  return (
    <div style={styles.detailChips}>
      {parts.map((part, idx) => (
        <span key={`${part.key || "d"}-${idx}`} style={styles.detailChip}>
          {part.key ? (
            <>
              <span style={styles.detailKey}>{part.key}:</span>
              <span style={styles.detailValue}>{part.value || "-"}</span>
            </>
          ) : (
            <span style={styles.detailValue}>{part.value}</span>
          )}
        </span>
      ))}
    </div>
  );
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isPending, startTransition] = useTransition();

  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activityFilter, setActivityFilter] = useState("app"); // app | auth | all
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);

      const token = AuthService?.getAccessToken();

      const rawBaseUrl = trimQuotes(process.env.REACT_APP_KEYCLOAK_SERVER);
      const realm = trimQuotes(process.env.REACT_APP_REALM_NAME);
      const baseUrl = rawBaseUrl ? rawBaseUrl.replace(/\/+$/, "") : "";

      const auditUrl = `${process.env.REACT_APP_API_BASE_URL}/auditLogs?all=true`;

      const keycloakUrl =
        baseUrl && realm
          ? `${baseUrl}/admin/realms/${encodeURIComponent(realm)}/events?max=200`
          : null;

      const requests = [
        fetch(auditUrl, {
          headers: {
            Accept: "application/json",
          },
        }),
      ];

      if (keycloakUrl && token) {
        requests.push(
          fetch(keycloakUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          })
        );
      }

      const responses = await Promise.all(requests);
      const auditResponse = responses[0];
      const keycloakResponse = responses[1];

      const auditResult = auditResponse.ok ? await safeJsonParse(auditResponse) : [];

      let keycloakRawEvents = [];
      if (keycloakResponse && keycloakResponse.ok) {
        const parsed = await safeJsonParse(keycloakResponse);
        keycloakRawEvents = Array.isArray(parsed) ? parsed : [];
      }

      const auditData = Array.isArray(auditResult) ? auditResult : [];

      const auditLogs = auditData.flatMap((item) =>
        (item.logs || []).map((log) => {
          const moduleLabel = getAuditLogModuleLabel(log.path);
          return {
            timestamp: log.timestamp || null,
            date: formatDateLocal(log.timestamp),
            user: log.email || "newuser",
            action: log.method || "Request",
            module: moduleLabel,
            modulePath: log.path || "",
            status_code:
              typeof log.status_code === "number" ? log.status_code : 200,
            status_label:
              typeof log.status_code === "number" && log.status_code >= 400
                ? "Failed"
                : "Success",
            details: [
              log.path ? `Endpoint: ${log.path}` : null,
              log.query_string ? `Query: ${log.query_string}` : null,
              log.duration_ms || log.duration_ms === 0
                ? `Duration: ${log.duration_ms} ms`
                : null,
              log.client_ip ? `IP: ${log.client_ip}` : null,
            ]
              .filter(Boolean)
              .join(" | "),
            request_id: log.request_id || "-",
          };
        })
      );

      const uniqueUserIds = [
        ...new Set(keycloakRawEvents.map((item) => item.userId).filter(Boolean)),
      ];

      const userMap = {};

      await Promise.all(
        uniqueUserIds.map(async (userId) => {
          try {
            if (!baseUrl || !realm || !token) {
              userMap[userId] = userId;
              return;
            }

            const userResponse = await fetch(
              `${baseUrl}/admin/realms/${encodeURIComponent(
                realm
              )}/users/${encodeURIComponent(userId)}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  Accept: "application/json",
                },
              }
            );

            if (userResponse.ok) {
              const userData = await safeJsonParse(userResponse);
              userMap[userId] = userData?.email || userData?.username || userId;
            } else {
              userMap[userId] = userId;
            }
          } catch (err) {
            userMap[userId] = userId;
          }
        })
      );

      const authLogs = keycloakRawEvents
        .filter((item) => item.type !== "CODE_TO_TOKEN")
        .map((item) => {
          const isError =
            String(item.type || "").includes("_ERROR") || !!item.error;

          const username =
            item?.details?.username || userMap[item.userId] || "Anonymous";

          return {
            timestamp: item.time ? new Date(item.time).toISOString() : null,
            date: formatDateLocal(item.time),
            user: userMap[item.userId] || username || "Anonymous",
            action: getReadableAction(item.type),
            module: item.clientId || "Authentication",
            status_code: isError ? 401 : 200,
            status_label: isError ? "Failed" : "Success",
            details: [
              username ? `Username: ${username}` : null,
              item.clientId ? `Client: ${item.clientId}` : null,
              item.ipAddress ? `IP: ${item.ipAddress}` : null,
              item?.details?.auth_method
                ? `Auth: ${item.details.auth_method}`
                : null,
              item?.details?.grant_type
                ? `Grant: ${item.details.grant_type}`
                : null,
              item.error && item.error !== "-" ? `Error: ${item.error}` : null,
            ]
              .filter(Boolean)
              .join(" | "),
            request_id: item.id || `kc-${item.time || Math.random()}`,
          };
        });

      const mergedLogs = [...auditLogs, ...authLogs].sort((a, b) => {
        const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return bTime - aTime;
      });

      startTransition(() => {
        setLogs(mergedLogs);
        setCurrentPage(1);
        setLastUpdated(new Date());
      });
    } catch (error) {
      console.error("Error fetching logs:", error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const totalApiHits = logs.length;

  const uniqueApis = useMemo(
    () => new Set(logs.map((l) => l.module).filter(Boolean)).size,
    [logs]
  );

  const activeUsers = useMemo(
    () => new Set(logs.map((l) => l.user).filter(Boolean)).size,
    [logs]
  );

  const failedRequests = useMemo(
    () => logs.filter((l) => l.status_code >= 400).length,
    [logs]
  );

  const dateOptions = useMemo(() => {
    return [...new Set(logs.map((l) => l.date).filter(Boolean))].sort((a, b) => {
      return new Date(b).getTime() - new Date(a).getTime();
    });
  }, [logs]);

  const filteredLogs = useMemo(() => {
    const term = search.trim().toLowerCase();

    return logs.filter((log) => {
      if (dateFilter !== "all" && log.date !== dateFilter) return false;

      if (statusFilter === "success" && log.status_code >= 400) return false;
      if (statusFilter === "failed" && log.status_code < 400) return false;

      const isAuth = isAuthSignAction(log.action);
      if (activityFilter === "app" && isAuth) return false;
      if (activityFilter === "auth" && !isAuth) return false;

      if (!term) return true;

      return [log.user, log.action, log.module, log.modulePath, log.details]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term));
    });
  }, [logs, search, dateFilter, statusFilter, activityFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / ROWS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStart = (safeCurrentPage - 1) * ROWS_PER_PAGE;
  const pageRows = filteredLogs.slice(pageStart, pageStart + ROWS_PER_PAGE);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, dateFilter, statusFilter, activityFilter]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      startTransition(() => setCurrentPage(page));
    }
  };

  const resetFilters = () => {
    startTransition(() => {
      setSearch("");
      setDateFilter("all");
      setStatusFilter("all");
      setActivityFilter("app");
      setCurrentPage(1);
    });
  };

  const downloadExcel = () => {
    const formatted = filteredLogs.map((log) => ({
      Date: log.date || "-",
      Time: formatTimeLocal(log.timestamp),
      User: log.user || "Anonymous",
      Action: log.action || "-",
      Module: log.module || "-",
      Status: log.status_label || "-",
      Details: log.details || "-",
      Request_ID: log.request_id || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formatted);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "AuditLogs");
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `audit-logs-${stamp}.xlsx`);
  };

  const showingFrom = filteredLogs.length === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(pageStart + ROWS_PER_PAGE, filteredLogs.length);

  return (
    <>
      <div style={styles.wrapper}>
        <PageHeader
          title="Audit Logs"
          subtitle="Track API usage, users, authentication events and performance across the platform."
          style={{ marginBottom: 14, paddingBottom: 12 }}
        />

        {/* KPI CARDS
        <div style={styles.kpiGrid}>
          <KpiCard Icon={FaChartLine} label="Total API Hits" value={totalApiHits} />
          <KpiCard Icon={FaNetworkWired} label="Unique APIs" value={uniqueApis} />
          <KpiCard Icon={FaUsers} label="Active Users" value={activeUsers} />
          <KpiCard Icon={FaExclamationTriangle} label="Failed Requests" value={failedRequests} />
        </div> */}

        <div style={styles.card}>
          {/* FILTER BAR */}
          <div style={styles.filterBar}>
            <div style={styles.searchGroup}>
              <input
                placeholder="Search by user, action, module or details..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={styles.searchInput}
              />
            </div>

            <div style={styles.filterActions}>
              <select
                value={activityFilter}
                onChange={(e) => setActivityFilter(e.target.value)}
                style={styles.select}
              >
                <option value="app">App activity</option>
                <option value="auth">Sign-in / Sign-out</option>
                <option value="all">All actions</option>
              </select>

              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                style={styles.select}
              >
                <option value="all">All Dates</option>
                {dateOptions.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={styles.select}
              >
                <option value="all">All Status</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>

              <button type="button" onClick={resetFilters} style={styles.resetBtn}>
                Reset Filter
              </button>

              <button
                type="button"
                onClick={downloadExcel}
                disabled={filteredLogs.length === 0}
                style={{
                  ...styles.downloadBtn,
                  ...(filteredLogs.length === 0 ? styles.btnDisabled : {}),
                }}
              >
                Download Excel
              </button>
            </div>
          </div>

          {(loading || isPending) && (
            <div
              style={{
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                color: "#9a3412",
                padding: "10px 14px",
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 13,
                fontWeight: 500,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              Loading logs...
            </div>
          )}

          {/* TABLE */}
          <div style={styles.tableWrap}>
            <div style={styles.tableScroll}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Time</th>
                    <th style={styles.th}>User</th>
                    {/* <th style={styles.th}>Action</th> */}
                    <th style={styles.th}>Module</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length > 0 ? (
                    pageRows.map((log, index) => (
                      <tr
                        key={`${log.request_id}-${index}`}
                        style={{ background: index % 2 === 0 ? "#fafbfc" : "#fff" }}
                      >
                        <td style={styles.tdBold}>{log.date}</td>
                        <td style={{ ...styles.td, whiteSpace: "nowrap" }}>
                          {formatTimeLocal(log.timestamp)}
                        </td>
                        <td style={styles.tdBold}>{log.user || "Anonymous"}</td>
                        {/* <td style={styles.tdBold}>{log.action}</td> */}
                        <td style={styles.td}>{log.module}</td>
                        <td style={styles.td}>
                          <StatusTag statusCode={log.status_code} label={log.status_label} />
                        </td>
                        <td style={styles.tdLeft}>
                          <DetailsCell details={log.details} />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        style={{
                          textAlign: "center",
                          padding: "40px 16px",
                          fontSize: 14,
                          color: "#6a7d8c",
                          fontFamily: "Poppins, sans-serif",
                        }}
                      >
                        {loading ? "Loading logs..." : "No logs found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* META ROW */}
          <div style={styles.metaRow}>
            <p style={{ margin: 0 }}>
              Showing {showingFrom}&ndash;{showingTo} of {filteredLogs.length} logs
            </p>
            <p style={{ margin: 0 }}>
              {lastUpdated
                ? `Last updated: ${lastUpdated.toLocaleTimeString()}`
                : "Waiting for first sync..."}
            </p>
          </div>

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                type="button"
                onClick={() => handlePageChange(safeCurrentPage - 1)}
                disabled={safeCurrentPage === 1}
                style={{
                  ...styles.pageBtn,
                  opacity: safeCurrentPage === 1 ? 0.5 : 1,
                  cursor: safeCurrentPage === 1 ? "not-allowed" : "pointer",
                }}
              >
                Previous
              </button>

              {getVisiblePages(safeCurrentPage, totalPages).map((pageNum) => (
                <button
                  type="button"
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  style={{
                    ...styles.pageBtn,
                    ...(safeCurrentPage === pageNum ? styles.pageBtnActive : {}),
                  }}
                >
                  {pageNum}
                </button>
              ))}

              <button
                type="button"
                onClick={() => handlePageChange(safeCurrentPage + 1)}
                disabled={safeCurrentPage === totalPages}
                style={{
                  ...styles.pageBtn,
                  opacity: safeCurrentPage === totalPages ? 0.5 : 1,
                  cursor: safeCurrentPage === totalPages ? "not-allowed" : "pointer",
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AuditLogs;
